import React, { useState } from "react";
import { Loader2, PhoneOutgoing } from "lucide-react";
import type CampaignContact from "@/model/interface/CampaignContact";
import { usePowerDialer } from "@/context/PowerDialerContext";
import { getContactDisplayName } from "@/util/string";
import { formatClockTime } from "@/util/date";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const DAILY_CAP_HOLD = "DAILY_CAP";

export function HeldLeadItem({ lead }: { lead: CampaignContact }) {
  const { callLeadAnyway } = usePowerDialer();
  const [confirmation, setConfirmation] = useState<string | null>(null);
  const [isCalling, setIsCalling] = useState(false);
  const displayName = (lead.contact && getContactDisplayName(lead.contact)) ?? lead.phoneNumber ?? "Unknown";
  const confirmationTitle =
    lead.holdReason === DAILY_CAP_HOLD ? "Call past the daily call limit?" : "Call outside calling hours?";

  const handleCall = async (acknowledgedCallingRules: boolean) => {
    setIsCalling(true);
    try {
      const explanation = await callLeadAnyway({ id: lead.id }, acknowledgedCallingRules);
      setConfirmation(explanation);
    } finally {
      setIsCalling(false);
    }
  };

  return (
    <div className="rounded-md px-1 py-2.5">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="truncate text-base font-semibold dark:text-white">{displayName}</p>
          {lead.holdDetail && <p className="text-sm text-muted-foreground">{lead.holdDetail}</p>}
          {lead.holdUntil && (
            <p className="text-sm text-muted-foreground">Callable from {formatClockTime(lead.holdUntil)}</p>
          )}
        </div>
        <Button
          size="sm"
          variant="outline"
          className="shrink-0"
          onClick={() => void handleCall(false)}
          disabled={isCalling}
          title="Call this lead now"
        >
          {isCalling ? <Loader2 className="h-4 w-4 animate-spin" /> : <PhoneOutgoing className="mr-1 h-4 w-4" />}
          Call anyway
        </Button>
      </div>

      <AlertDialog open={confirmation !== null} onOpenChange={(open) => !open && setConfirmation(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmationTitle}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmation} The call is recorded with your name and the lead's local time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void handleCall(true)}>Call anyway</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
